import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import { portfolioData } from '../mock';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-black via-zinc-900 to-black" />
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-amber-400/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 text-center">
          <div
            className={`transform transition-all duration-1000 ${
              isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-amber-400/20 rounded-full mb-8">
              <Sparkles size={16} className="text-amber-400" />
              <span className="text-sm text-amber-400 font-medium">Visual Designer & Creative Director</span>
            </div>

            {/* Name with Photo */}
            <div className="flex items-center justify-center gap-8 mb-6">
              <div className="relative flex-shrink-0">
                <img
                  src="/images/scott-parrish.png"
                  alt="Scott Parrish"
                  className="w-32 md:w-40 h-auto object-contain"
                />
              </div>
              
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight text-left">
                <span className="block text-white mb-2">SCOTT</span>
                <span className="block bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500 bg-clip-text text-transparent">
                  PARRISH
                </span>
              </h1>
            </div>

            <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-3xl mx-auto font-light tracking-wide">
              {portfolioData.philosophy}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/work"
                className="group relative px-8 py-4 bg-amber-400 text-black font-semibold rounded-full hover:bg-amber-300 transition-all duration-300 overflow-hidden"
              >
                <span className="relative z-10 flex items-center gap-2 justify-center">
                  View My Work
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </Link>
              <Link
                to="/contact"
                className="px-8 py-4 bg-white/5 backdrop-blur-sm border border-white/10 text-white font-semibold rounded-full hover:bg-white/10 hover:border-amber-400/50 transition-all duration-300 text-center"
              >
                Get In Touch
              </Link>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-amber-400/30 rounded-full flex items-start justify-center p-2">
              <div className="w-1.5 h-3 bg-amber-400 rounded-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Work Preview */}
      <section className="py-24 bg-gradient-to-b from-black to-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Featured <span className="text-amber-400">Projects</span>
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              A curated selection of my latest work, showcasing the evolution of visual knowledge across various mediums.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioData.projects.slice(0, 6).map((project, index) => (
              <Link
                key={project.id}
                to={`/work/${project.id}`}
                className="group relative overflow-hidden rounded-2xl bg-zinc-900 border border-white/5 hover:border-amber-400/30 transition-all duration-500"
                style={{
                  animationDelay: `${index * 100}ms`
                }}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    crossOrigin="anonymous"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-6 group-hover:translate-y-0 transition-transform duration-500">
                  <p className="text-amber-400 text-sm font-semibold mb-1">{project.category}</p>
                  <h3 className="text-white text-xl font-bold">{project.title}</h3>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/work"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 backdrop-blur-sm border border-white/10 text-white font-semibold rounded-full hover:bg-white/10 hover:border-amber-400/50 transition-all duration-300"
            >
              View All Projects
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 bg-zinc-900">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-sm border border-white/10 rounded-3xl p-12">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Design <span className="text-amber-400">Philosophy</span>
              </h2>
              <div className="w-20 h-1 bg-amber-400 mx-auto rounded-full" />
            </div>
            <blockquote className="text-xl md:text-2xl text-gray-300 leading-relaxed text-center italic">
              "{portfolioData.philosophy}"
            </blockquote>
            <p className="text-gray-400 mt-8 text-center max-w-3xl mx-auto">
              This guiding principle pushes me to explore new techniques, experiment with diverse mediums, 
              and consistently seek a deeper understanding of the visual language I employ.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;